import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TechLayout from '../../components/tech/TechLayout';
import { getWorkOrder, getPhotos, uploadPhoto, deletePhoto } from '../../api/client';

export default function TechJobPhotos() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [manageMode, setManageMode] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1', '');

  const load = async () => {
    try {
      setLoading(true);
      const [jobRes, photoRes] = await Promise.all([
        getWorkOrder(id),
        getPhotos(id),
      ]);
      setJob(jobRes.data);
      setPhotos(photoRes.data || []);
    } catch (err) {
      console.error('load photos page failed:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      setMessage('');
      const fd = new FormData();
      fd.append('file', file);
      await uploadPhoto(id, fd);
      setMessage('Upload successful');
      await load();
    } catch (err) {
      console.error('upload failed:', err.response?.data || err);
      setMessage('Upload failed, please try again');
    } finally {
      setUploading(false);
      e.target.value = '';
      setTimeout(() => setMessage(''), 2500);
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await deletePhoto(id, photoId);
      setPhotos(p => p.filter(ph => ph.id !== photoId));
    } catch (err) {
      console.error('delete failed:', err.response?.data || err);
      setMessage('Delete failed');
      setTimeout(() => setMessage(''), 2500);
    }
  };

  return (
    <TechLayout title="Photos">
      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <img
            src={lightboxUrl}
            alt="preview"
            style={{ maxWidth: '95vw', maxHeight: '88vh', borderRadius: 8, objectFit: 'contain' }}
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxUrl(null)}
            style={{ position: 'fixed', top: 16, right: 18, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 26, fontWeight: 700, cursor: 'pointer', borderRadius: '50%', width: 40, height: 40, lineHeight: '40px', textAlign: 'center', padding: 0 }}
          >
            ×
          </button>
        </div>
      )}

      <button
        onClick={() => navigate(`/tech/jobs/${id}`)}
        style={{ marginBottom: 14, border: 'none', background: '#F3F4F6', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
      >
        ← Back
      </button>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading...</div>
      ) : (
        <>
          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Job</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{job?.job_number || `WO-${job?.id}`}</div>
            <div style={{ fontSize: 14, color: '#374151', marginTop: 2 }}>{job?.customer_name || 'Unknown Customer'}</div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Upload Photo</div>

            <label
              style={{
                display: 'block', width: '100%', textAlign: 'center',
                background: uploading ? '#93C5FD' : '#2563EB',
                color: '#fff', borderRadius: 10, padding: '14px 16px',
                fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer',
                boxSizing: 'border-box',
              }}
            >
              {uploading ? 'Uploading...' : 'Take / Upload Photo'}
              <input type="file" accept="image/*" capture="environment" onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
            </label>

            {message && (
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: message === 'Upload successful' ? '#059669' : '#DC2626' }}>
                {message}
              </div>
            )}
          </div>

          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>
                Uploaded Photos ({photos.length})
              </div>
              {photos.length > 0 && (
                <button
                  onClick={() => setManageMode(m => !m)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: manageMode ? '#FEF2F2' : '#F3F4F6',
                    color: manageMode ? '#DC2626' : '#374151',
                    border: manageMode ? '1px solid #FECACA' : '1px solid #E5E7EB',
                  }}
                >
                  {manageMode ? 'Done' : 'Manage'}
                </button>
              )}
            </div>

            {photos.length === 0 ? (
              <div style={{ color: '#6B7280', fontSize: 14 }}>No photos uploaded yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {photos.map((ph) => (
                  <div
                    key={ph.id}
                    style={{ border: manageMode ? '2px solid #FECACA' : '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#F9FAFB' }}
                  >
                    <img
                      src={`${API_BASE}${ph.url}`}
                      alt={ph.caption || 'photo'}
                      onClick={() => !manageMode && setLightboxUrl(`${API_BASE}${ph.url}`)}
                      style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block', cursor: manageMode ? 'default' : 'zoom-in' }}
                    />
                    <div style={{ padding: 12 }}>
                      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: manageMode ? 10 : 0 }}>
                        {ph.caption || 'No caption'}
                      </div>
                      {manageMode && (
                        <button
                          onClick={() => handleDelete(ph.id)}
                          style={{ width: '100%', border: 'none', background: '#FEE2E2', color: '#DC2626', borderRadius: 8, padding: '10px 12px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Delete Photo
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </TechLayout>
  );
}
